import React, { createContext, useContext, useState, useCallback } from 'react';

export interface MinimizedModal {
  id: string;
  title: string;
  icon?: React.ReactNode;
  onRestore: () => void;
  toolbarActionId?: string; // Maps to toolbar action ID
}

interface ModalManagerContextType {
  minimizedModals: MinimizedModal[];
  minimizeModal: (modal: MinimizedModal) => void;
  restoreModal: (id: string) => void;
  removeModal: (id: string) => void;
  getMinimizedModalByActionId: (actionId: string) => MinimizedModal | undefined;
}

const ModalManagerContext = createContext<ModalManagerContextType | undefined>(undefined);

export const useModalManager = () => {
  const context = useContext(ModalManagerContext);
  if (!context) {
    throw new Error('useModalManager must be used within a ModalManagerProvider');
  }
  return context;
};

/**
 * Optional version of useModalManager that doesn't throw error
 * Returns undefined if not within provider
 */
export const useOptionalModalManager = () => {
  return useContext(ModalManagerContext);
};

export const ModalManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [minimizedModals, setMinimizedModals] = useState<MinimizedModal[]>([]);

  const minimizeModal = useCallback((modal: MinimizedModal) => {
    setMinimizedModals(prev => {
      const existing = prev.find(m => m.id === modal.id);
      const newState = existing 
        ? prev.map(m => m.id === modal.id ? modal : m)
        : [...prev, modal];
      return newState;
    });
  }, []);

  const restoreModal = useCallback((id: string) => {
    const modal = minimizedModals.find(m => m.id === id);
    if (modal) {
      modal.onRestore();
      setMinimizedModals(prev => prev.filter(m => m.id !== id));
    }
  }, [minimizedModals]);

  const removeModal = useCallback((id: string) => {
    setMinimizedModals(prev => prev.filter(m => m.id !== id));
  }, []);

  const getMinimizedModalByActionId = useCallback((actionId: string) => {
    return minimizedModals.find(modal => modal.toolbarActionId === actionId);
  }, [minimizedModals]);

  return (
    <ModalManagerContext.Provider value={{
      minimizedModals,
      minimizeModal,
      restoreModal,
      removeModal,
      getMinimizedModalByActionId
    }}>
      {children}
    </ModalManagerContext.Provider>
  );
};
